// cspell:disable

/**
 *  List of USA state postal codes (same as ISO 3166-2 subdivision code)
 */
export const usaProvinces = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "American Samoa", code: "AS" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "Armed Forces Europe", code: "AE" },
  { name: "Armed Forces Pacific", code: "AP" },
  { name: "Armed Forces America", code: "AA" },
  { name: "Armed Forces Americas", code: "AA" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "District of Columbia", code: "DC" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Guam", code: "GU" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "Northern Mariana Islands", code: "MP" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Puerto Rico", code: "PR" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "United States Minor Outlying Islands", code: "UM" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Virgin Islands, U.S.", code: "VI" },
  { name: "Virgin Islands", code: "VI" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
] as const;

export const USA_PROVINCE_CODES = usaProvinces.map((province) => province.code);
export const USA_PROVINCE_NAMES = usaProvinces.map((province) => province.name);

export type USAProvinceCode = (typeof usaProvinces)[number]["code"];
export type USAProvinceName = (typeof usaProvinces)[number]["name"];

// 49 contiguous states (all states + DC - Hawaii - Alaska)
export const CONTIGUOUS_USA_PROVINCES: USAProvinceCode[] = [
  "AL",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export const USA_PROVINCES_BY_CODE = Object.fromEntries(
  usaProvinces.map((country) => [country.code, country.name]),
);
export const USA_PROVINCES_BY_NAME = Object.fromEntries(
  usaProvinces.map((country) => [country.name.toLowerCase(), country.code]),
);

export const findProvinceCode = (province: string) => {
  if (USA_PROVINCES_BY_CODE[province]) {
    return province;
  }
  const code = USA_PROVINCES_BY_NAME[province.toLowerCase()];

  if (code === undefined) {
    return province;
  }
  return code;
};

export function isUsaProvinceCode(
  province: string,
): province is USAProvinceCode {
  return USA_PROVINCE_CODES.includes(province as USAProvinceCode);
}

/**
 * Provinces that Shopify recognizes. A good starting place for provinces in the world.
 * source: https://matrixify.app/documentation/shopify-address-countries-and-provinces/
 */
export const shopifyProvinces: {
  country: string;
  countryCode: string;
  province: string;
}[] = [
  { country: "Argentina", countryCode: "AR", province: "Buenos Aires" },
  { country: "Argentina", countryCode: "AR", province: "Catamarca" },
  { country: "Argentina", countryCode: "AR", province: "Chaco" },
  { country: "Argentina", countryCode: "AR", province: "Chubut" },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Ciudad Autónoma de Buenos Aires",
  },
  { country: "Argentina", countryCode: "AR", province: "Córdoba" },
  { country: "Argentina", countryCode: "AR", province: "Corrientes" },
  { country: "Argentina", countryCode: "AR", province: "Entre Ríos" },
  { country: "Argentina", countryCode: "AR", province: "Formosa" },
  { country: "Argentina", countryCode: "AR", province: "Jujuy" },
  { country: "Argentina", countryCode: "AR", province: "La Pampa" },
  { country: "Argentina", countryCode: "AR", province: "La Rioja" },
  { country: "Argentina", countryCode: "AR", province: "Mendoza" },
  { country: "Argentina", countryCode: "AR", province: "Misiones" },
  { country: "Argentina", countryCode: "AR", province: "Neuquén" },
  { country: "Argentina", countryCode: "AR", province: "Río Negro" },
  { country: "Argentina", countryCode: "AR", province: "Salta" },
  { country: "Argentina", countryCode: "AR", province: "San Juan" },
  { country: "Argentina", countryCode: "AR", province: "San Luis" },
  { country: "Argentina", countryCode: "AR", province: "Santa Cruz" },
  { country: "Argentina", countryCode: "AR", province: "Santa Fe" },
  { country: "Argentina", countryCode: "AR", province: "Santiago Del Estero" },
  { country: "Argentina", countryCode: "AR", province: "Tierra Del Fuego" },
  { country: "Argentina", countryCode: "AR", province: "Tucumán" },
  {
    country: "Australia",
    countryCode: "AU",
    province: "Australian Capital Territory",
  },
  { country: "Australia", countryCode: "AU", province: "New South Wales" },
  { country: "Australia", countryCode: "AU", province: "Northern Territory" },
  { country: "Australia", countryCode: "AU", province: "Queensland" },
  { country: "Australia", countryCode: "AU", province: "South Australia" },
  { country: "Australia", countryCode: "AU", province: "Tasmania" },
  { country: "Australia", countryCode: "AU", province: "Victoria" },
  { country: "Australia", countryCode: "AU", province: "Western Australia" },
  { country: "Brazil", countryCode: "BR", province: "Acre" },
  { country: "Brazil", countryCode: "BR", province: "Alagoas" },
  { country: "Brazil", countryCode: "BR", province: "Amapá" },
  { country: "Brazil", countryCode: "BR", province: "Amazonas" },
  { country: "Brazil", countryCode: "BR", province: "Bahia" },
  { country: "Brazil", countryCode: "BR", province: "Ceará" },
  { country: "Brazil", countryCode: "BR", province: "Distrito Federal" },
  { country: "Brazil", countryCode: "BR", province: "Espírito Santo" },
  { country: "Brazil", countryCode: "BR", province: "Goiás" },
  { country: "Brazil", countryCode: "BR", province: "Maranhão" },
  { country: "Brazil", countryCode: "BR", province: "Mato Grosso" },
  { country: "Brazil", countryCode: "BR", province: "Mato Grosso do Sul" },
  { country: "Brazil", countryCode: "BR", province: "Minas Gerais" },
  { country: "Brazil", countryCode: "BR", province: "Pará" },
  { country: "Brazil", countryCode: "BR", province: "Paraíba" },
  { country: "Brazil", countryCode: "BR", province: "Paraná" },
  { country: "Brazil", countryCode: "BR", province: "Pernambuco" },
  { country: "Brazil", countryCode: "BR", province: "Piauí" },
  { country: "Brazil", countryCode: "BR", province: "Rio de Janeiro" },
  { country: "Brazil", countryCode: "BR", province: "Rio Grande do Norte" },
  { country: "Brazil", countryCode: "BR", province: "Rio Grande do Sul" },
  { country: "Brazil", countryCode: "BR", province: "Rondônia" },
  { country: "Brazil", countryCode: "BR", province: "Roraima" },
  { country: "Brazil", countryCode: "BR", province: "Santa Catarina" },
  { country: "Brazil", countryCode: "BR", province: "São Paulo" },
  { country: "Brazil", countryCode: "BR", province: "Sergipe" },
  { country: "Brazil", countryCode: "BR", province: "Tocantins" },
  { country: "Canada", countryCode: "CA", province: "Alberta" },
  { country: "Canada", countryCode: "CA", province: "British Columbia" },
  { country: "Canada", countryCode: "CA", province: "Manitoba" },
  { country: "Canada", countryCode: "CA", province: "New Brunswick" },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Newfoundland and Labrador",
  },
  { country: "Canada", countryCode: "CA", province: "Northwest Territories" },
  { country: "Canada", countryCode: "CA", province: "Nova Scotia" },
  { country: "Canada", countryCode: "CA", province: "Nunavut" },
  { country: "Canada", countryCode: "CA", province: "Ontario" },
  { country: "Canada", countryCode: "CA", province: "Prince Edward Island" },
  { country: "Canada", countryCode: "CA", province: "Quebec" },
  { country: "Canada", countryCode: "CA", province: "Saskatchewan" },
  { country: "Canada", countryCode: "CA", province: "Yukon" },
  { country: "Chile", countryCode: "CL", province: "Arica and Parinacota" },
  { country: "Chile", countryCode: "CL", province: "Tarapacá" },
  { country: "Chile", countryCode: "CL", province: "Antofagasta" },
  { country: "Chile", countryCode: "CL", province: "Atacama" },
  { country: "Chile", countryCode: "CL", province: "Coquimbo" },
  { country: "Chile", countryCode: "CL", province: "Valparaíso" },
  { country: "Chile", countryCode: "CL", province: "Santiago" },
  { country: "Chile", countryCode: "CL", province: "O’Higgins" },
  { country: "Chile", countryCode: "CL", province: "Maule" },
  { country: "Chile", countryCode: "CL", province: "Ñuble" },
  { country: "Chile", countryCode: "CL", province: "Biobío" },
  { country: "Chile", countryCode: "CL", province: "Araucanía" },
  { country: "Chile", countryCode: "CL", province: "Los Ríos" },
  { country: "Chile", countryCode: "CL", province: "Los Lagos" },
  { country: "Chile", countryCode: "CL", province: "Aysén" },
  { country: "Chile", countryCode: "CL", province: "Magallanes" },
  { country: "China", countryCode: "CN", province: "Anhui" },
  { country: "China", countryCode: "CN", province: "Beijing" },
  { country: "China", countryCode: "CN", province: "Chongqing" },
  { country: "China", countryCode: "CN", province: "Fujian" },
  { country: "China", countryCode: "CN", province: "Gansu" },
  { country: "China", countryCode: "CN", province: "Guangdong" },
  { country: "China", countryCode: "CN", province: "Guangxi" },
  { country: "China", countryCode: "CN", province: "Guizhou" },
  { country: "China", countryCode: "CN", province: "Hainan" },
  { country: "China", countryCode: "CN", province: "Hebei" },
  { country: "China", countryCode: "CN", province: "Heilongjiang" },
  { country: "China", countryCode: "CN", province: "Henan" },
  { country: "China", countryCode: "CN", province: "Hubei" },
  { country: "China", countryCode: "CN", province: "Hunan" },
  { country: "China", countryCode: "CN", province: "Inner Mongolia" },
  { country: "China", countryCode: "CN", province: "Jiangsu" },
  { country: "China", countryCode: "CN", province: "Jiangxi" },
  { country: "China", countryCode: "CN", province: "Jilin" },
  { country: "China", countryCode: "CN", province: "Liaoning" },
  { country: "China", countryCode: "CN", province: "Ningxia" },
  { country: "China", countryCode: "CN", province: "Qinghai" },
  { country: "China", countryCode: "CN", province: "Shaanxi" },
  { country: "China", countryCode: "CN", province: "Shandong" },
  { country: "China", countryCode: "CN", province: "Shanghai" },
  { country: "China", countryCode: "CN", province: "Shanxi" },
  { country: "China", countryCode: "CN", province: "Sichuan" },
  { country: "China", countryCode: "CN", province: "Tianjin" },
  { country: "China", countryCode: "CN", province: "Xinjiang" },
  { country: "China", countryCode: "CN", province: "Xizang" },
  { country: "China", countryCode: "CN", province: "Yunnan" },
  { country: "China", countryCode: "CN", province: "Zhejiang" },
  { country: "Colombia", countryCode: "CO", province: "Amazonas" },
  { country: "Colombia", countryCode: "CO", province: "Antioquia" },
  { country: "Colombia", countryCode: "CO", province: "Arauca" },
  { country: "Colombia", countryCode: "CO", province: "Atlántico" },
  { country: "Colombia", countryCode: "CO", province: "Bogotá, D.C." },
  { country: "Colombia", countryCode: "CO", province: "Bolívar" },
  { country: "Colombia", countryCode: "CO", province: "Boyacá" },
  { country: "Colombia", countryCode: "CO", province: "Caldas" },
  { country: "Colombia", countryCode: "CO", province: "Caquetá" },
  { country: "Colombia", countryCode: "CO", province: "Casanare" },
  { country: "Colombia", countryCode: "CO", province: "Cauca" },
  { country: "Colombia", countryCode: "CO", province: "Cesar" },
  { country: "Colombia", countryCode: "CO", province: "Chocó" },
  { country: "Colombia", countryCode: "CO", province: "Córdoba" },
  { country: "Colombia", countryCode: "CO", province: "Cundinamarca" },
  { country: "Colombia", countryCode: "CO", province: "Guainía" },
  { country: "Colombia", countryCode: "CO", province: "Guaviare" },
  { country: "Colombia", countryCode: "CO", province: "Huila" },
  { country: "Colombia", countryCode: "CO", province: "La Guajira" },
  { country: "Colombia", countryCode: "CO", province: "Magdalena" },
  { country: "Colombia", countryCode: "CO", province: "Meta" },
  { country: "Colombia", countryCode: "CO", province: "Nariño" },
  { country: "Colombia", countryCode: "CO", province: "Norte de Santander" },
  { country: "Colombia", countryCode: "CO", province: "Putumayo" },
  { country: "Colombia", countryCode: "CO", province: "Quindío" },
  { country: "Colombia", countryCode: "CO", province: "Risaralda" },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "San Andrés, Providencia y Santa Catalina",
  },
  { country: "Colombia", countryCode: "CO", province: "Santander" },
  { country: "Colombia", countryCode: "CO", province: "Sucre" },
  { country: "Colombia", countryCode: "CO", province: "Tolima" },
  { country: "Colombia", countryCode: "CO", province: "Valle del Cauca" },
  { country: "Colombia", countryCode: "CO", province: "Vaupés" },
  { country: "Colombia", countryCode: "CO", province: "Vichada" },
  { country: "Egypt", countryCode: "EG", province: "6th of October" },
  { country: "Egypt", countryCode: "EG", province: "Al Sharqia" },
  { country: "Egypt", countryCode: "EG", province: "Alexandria" },
  { country: "Egypt", countryCode: "EG", province: "Aswan" },
  { country: "Egypt", countryCode: "EG", province: "Asyut" },
  { country: "Egypt", countryCode: "EG", province: "Beheira" },
  { country: "Egypt", countryCode: "EG", province: "Beni Suef" },
  { country: "Egypt", countryCode: "EG", province: "Cairo" },
  { country: "Egypt", countryCode: "EG", province: "Dakahlia" },
  { country: "Egypt", countryCode: "EG", province: "Damietta" },
  { country: "Egypt", countryCode: "EG", province: "Faiyum" },
  { country: "Egypt", countryCode: "EG", province: "Gharbia" },
  { country: "Egypt", countryCode: "EG", province: "Giza" },
  { country: "Egypt", countryCode: "EG", province: "Helwan" },
  { country: "Egypt", countryCode: "EG", province: "Ismailia" },
  { country: "Egypt", countryCode: "EG", province: "Kafr el-Sheikh" },
  { country: "Egypt", countryCode: "EG", province: "Luxor" },
  { country: "Egypt", countryCode: "EG", province: "Matrouh" },
  { country: "Egypt", countryCode: "EG", province: "Minya" },
  { country: "Egypt", countryCode: "EG", province: "Monufia" },
  { country: "Egypt", countryCode: "EG", province: "New Valley" },
  { country: "Egypt", countryCode: "EG", province: "North Sinai" },
  { country: "Egypt", countryCode: "EG", province: "Port Said" },
  { country: "Egypt", countryCode: "EG", province: "Qalyubia" },
  { country: "Egypt", countryCode: "EG", province: "Qena" },
  { country: "Egypt", countryCode: "EG", province: "Red Sea" },
  { country: "Egypt", countryCode: "EG", province: "Sohag" },
  { country: "Egypt", countryCode: "EG", province: "South Sinai" },
  { country: "Egypt", countryCode: "EG", province: "Suez" },
  { country: "Guatemala", countryCode: "GT", province: "Alta Verapaz" },
  { country: "Guatemala", countryCode: "GT", province: "Baja Verapaz" },
  { country: "Guatemala", countryCode: "GT", province: "Chimaltenango" },
  { country: "Guatemala", countryCode: "GT", province: "Chiquimula" },
  { country: "Guatemala", countryCode: "GT", province: "El Progreso" },
  { country: "Guatemala", countryCode: "GT", province: "Escuintla" },
  { country: "Guatemala", countryCode: "GT", province: "Guatemala" },
  { country: "Guatemala", countryCode: "GT", province: "Huehuetenango" },
  { country: "Guatemala", countryCode: "GT", province: "Izabal" },
  { country: "Guatemala", countryCode: "GT", province: "Jalapa" },
  { country: "Guatemala", countryCode: "GT", province: "Jutiapa" },
  { country: "Guatemala", countryCode: "GT", province: "Petén" },
  { country: "Guatemala", countryCode: "GT", province: "Quetzaltenango" },
  { country: "Guatemala", countryCode: "GT", province: "Quiché" },
  { country: "Guatemala", countryCode: "GT", province: "Retalhuleu" },
  { country: "Guatemala", countryCode: "GT", province: "Sacatepéquez" },
  { country: "Guatemala", countryCode: "GT", province: "San Marcos" },
  { country: "Guatemala", countryCode: "GT", province: "Santa Rosa" },
  { country: "Guatemala", countryCode: "GT", province: "Sololá" },
  { country: "Guatemala", countryCode: "GT", province: "Suchitepéquez" },
  { country: "Guatemala", countryCode: "GT", province: "Totonicapán" },
  { country: "Guatemala", countryCode: "GT", province: "Zacapa" },
  { country: "Hong Kong", countryCode: "HK", province: "Hong Kong Island" },
  { country: "Hong Kong", countryCode: "HK", province: "Kowloon" },
  { country: "Hong Kong", countryCode: "HK", province: "New Territories" },
  {
    country: "India",
    countryCode: "IN",
    province: "Andaman and Nicobar Islands",
  },
  { country: "India", countryCode: "IN", province: "Andhra Pradesh" },
  { country: "India", countryCode: "IN", province: "Arunachal Pradesh" },
  { country: "India", countryCode: "IN", province: "Assam" },
  { country: "India", countryCode: "IN", province: "Bihar" },
  { country: "India", countryCode: "IN", province: "Chandigarh" },
  { country: "India", countryCode: "IN", province: "Chhattisgarh" },
  { country: "India", countryCode: "IN", province: "Dadra and Nagar Haveli" },
  { country: "India", countryCode: "IN", province: "Daman and Diu" },
  { country: "India", countryCode: "IN", province: "Delhi" },
  { country: "India", countryCode: "IN", province: "Goa" },
  { country: "India", countryCode: "IN", province: "Gujarat" },
  { country: "India", countryCode: "IN", province: "Haryana" },
  { country: "India", countryCode: "IN", province: "Himachal Pradesh" },
  { country: "India", countryCode: "IN", province: "Jammu and Kashmir" },
  { country: "India", countryCode: "IN", province: "Jharkhand" },
  { country: "India", countryCode: "IN", province: "Karnataka" },
  { country: "India", countryCode: "IN", province: "Kerala" },
  { country: "India", countryCode: "IN", province: "Ladakh" },
  { country: "India", countryCode: "IN", province: "Lakshadweep" },
  { country: "India", countryCode: "IN", province: "Madhya Pradesh" },
  { country: "India", countryCode: "IN", province: "Maharashtra" },
  { country: "India", countryCode: "IN", province: "Manipur" },
  { country: "India", countryCode: "IN", province: "Meghalaya" },
  { country: "India", countryCode: "IN", province: "Mizoram" },
  { country: "India", countryCode: "IN", province: "Nagaland" },
  { country: "India", countryCode: "IN", province: "Odisha" },
  { country: "India", countryCode: "IN", province: "Puducherry" },
  { country: "India", countryCode: "IN", province: "Punjab" },
  { country: "India", countryCode: "IN", province: "Rajasthan" },
  { country: "India", countryCode: "IN", province: "Sikkim" },
  { country: "India", countryCode: "IN", province: "Tamil Nadu" },
  { country: "India", countryCode: "IN", province: "Telangana" },
  { country: "India", countryCode: "IN", province: "Tripura" },
  { country: "India", countryCode: "IN", province: "Uttar Pradesh" },
  { country: "India", countryCode: "IN", province: "Uttarakhand" },
  { country: "India", countryCode: "IN", province: "West Bengal" },
  { country: "Indonesia", countryCode: "ID", province: "Aceh" },
  { country: "Indonesia", countryCode: "ID", province: "Bali" },
  { country: "Indonesia", countryCode: "ID", province: "Bangka Belitung" },
  { country: "Indonesia", countryCode: "ID", province: "Banten" },
  { country: "Indonesia", countryCode: "ID", province: "Bengkulu" },
  { country: "Indonesia", countryCode: "ID", province: "Gorontalo" },
  { country: "Indonesia", countryCode: "ID", province: "Jakarta" },
  { country: "Indonesia", countryCode: "ID", province: "Jambi" },
  { country: "Indonesia", countryCode: "ID", province: "Jawa Barat" },
  { country: "Indonesia", countryCode: "ID", province: "Jawa Tengah" },
  { country: "Indonesia", countryCode: "ID", province: "Jawa Timur" },
  { country: "Indonesia", countryCode: "ID", province: "Kalimantan Barat" },
  { country: "Indonesia", countryCode: "ID", province: "Kalimantan Selatan" },
  { country: "Indonesia", countryCode: "ID", province: "Kalimantan Tengah" },
  { country: "Indonesia", countryCode: "ID", province: "Kalimantan Timur" },
  { country: "Indonesia", countryCode: "ID", province: "Kalimantan Utara" },
  { country: "Indonesia", countryCode: "ID", province: "Kepulauan Riau" },
  { country: "Indonesia", countryCode: "ID", province: "Lampung" },
  { country: "Indonesia", countryCode: "ID", province: "Maluku" },
  { country: "Indonesia", countryCode: "ID", province: "Maluku Utara" },
  { country: "Indonesia", countryCode: "ID", province: "North Sumatra" },
  { country: "Indonesia", countryCode: "ID", province: "Nusa Tenggara Barat" },
  { country: "Indonesia", countryCode: "ID", province: "Nusa Tenggara Timur" },
  { country: "Indonesia", countryCode: "ID", province: "Papua" },
  { country: "Indonesia", countryCode: "ID", province: "Papua Barat" },
  { country: "Indonesia", countryCode: "ID", province: "Riau" },
  { country: "Indonesia", countryCode: "ID", province: "South Sumatra" },
  { country: "Indonesia", countryCode: "ID", province: "Sulawesi Barat" },
  { country: "Indonesia", countryCode: "ID", province: "Sulawesi Selatan" },
  { country: "Indonesia", countryCode: "ID", province: "Sulawesi Tengah" },
  { country: "Indonesia", countryCode: "ID", province: "Sulawesi Tenggara" },
  { country: "Indonesia", countryCode: "ID", province: "Sulawesi Utara" },
  { country: "Indonesia", countryCode: "ID", province: "West Sumatra" },
  { country: "Indonesia", countryCode: "ID", province: "Yogyakarta" },
  { country: "Ireland", countryCode: "IE", province: "Carlow" },
  { country: "Ireland", countryCode: "IE", province: "Cavan" },
  { country: "Ireland", countryCode: "IE", province: "Clare" },
  { country: "Ireland", countryCode: "IE", province: "Cork" },
  { country: "Ireland", countryCode: "IE", province: "Donegal" },
  { country: "Ireland", countryCode: "IE", province: "Dublin" },
  { country: "Ireland", countryCode: "IE", province: "Galway" },
  { country: "Ireland", countryCode: "IE", province: "Kerry" },
  { country: "Ireland", countryCode: "IE", province: "Kildare" },
  { country: "Ireland", countryCode: "IE", province: "Kilkenny" },
  { country: "Ireland", countryCode: "IE", province: "Laois" },
  { country: "Ireland", countryCode: "IE", province: "Leitrim" },
  { country: "Ireland", countryCode: "IE", province: "Limerick" },
  { country: "Ireland", countryCode: "IE", province: "Longford" },
  { country: "Ireland", countryCode: "IE", province: "Louth" },
  { country: "Ireland", countryCode: "IE", province: "Mayo" },
  { country: "Ireland", countryCode: "IE", province: "Meath" },
  { country: "Ireland", countryCode: "IE", province: "Monaghan" },
  { country: "Ireland", countryCode: "IE", province: "Offaly" },
  { country: "Ireland", countryCode: "IE", province: "Roscommon" },
  { country: "Ireland", countryCode: "IE", province: "Sligo" },
  { country: "Ireland", countryCode: "IE", province: "Tipperary" },
  { country: "Ireland", countryCode: "IE", province: "Waterford" },
  { country: "Ireland", countryCode: "IE", province: "Westmeath" },
  { country: "Ireland", countryCode: "IE", province: "Wexford" },
  { country: "Ireland", countryCode: "IE", province: "Wicklow" },
  { country: "Italy", countryCode: "IT", province: "Agrigento" },
  { country: "Italy", countryCode: "IT", province: "Alessandria" },
  { country: "Italy", countryCode: "IT", province: "Ancona" },
  { country: "Italy", countryCode: "IT", province: "Aosta" },
  { country: "Italy", countryCode: "IT", province: "Arezzo" },
  { country: "Italy", countryCode: "IT", province: "Ascoli Piceno" },
  { country: "Italy", countryCode: "IT", province: "Asti" },
  { country: "Italy", countryCode: "IT", province: "Avellino" },
  { country: "Italy", countryCode: "IT", province: "Bari" },
  { country: "Italy", countryCode: "IT", province: "Barletta-Andria-Trani" },
  { country: "Italy", countryCode: "IT", province: "Belluno" },
  { country: "Italy", countryCode: "IT", province: "Benevento" },
  { country: "Italy", countryCode: "IT", province: "Bergamo" },
  { country: "Italy", countryCode: "IT", province: "Biella" },
  { country: "Italy", countryCode: "IT", province: "Bologna" },
  { country: "Italy", countryCode: "IT", province: "Bolzano" },
  { country: "Italy", countryCode: "IT", province: "Brescia" },
  { country: "Italy", countryCode: "IT", province: "Brindisi" },
  { country: "Italy", countryCode: "IT", province: "Cagliari" },
  { country: "Italy", countryCode: "IT", province: "Caltanissetta" },
  { country: "Italy", countryCode: "IT", province: "Campobasso" },
  { country: "Italy", countryCode: "IT", province: "Carbonia-Iglesias" },
  { country: "Italy", countryCode: "IT", province: "Caserta" },
  { country: "Italy", countryCode: "IT", province: "Catania" },
  { country: "Italy", countryCode: "IT", province: "Catanzaro" },
  { country: "Italy", countryCode: "IT", province: "Chieti" },
  { country: "Italy", countryCode: "IT", province: "Como" },
  { country: "Italy", countryCode: "IT", province: "Cosenza" },
  { country: "Italy", countryCode: "IT", province: "Cremona" },
  { country: "Italy", countryCode: "IT", province: "Crotone" },
  { country: "Italy", countryCode: "IT", province: "Cuneo" },
  { country: "Italy", countryCode: "IT", province: "Enna" },
  { country: "Italy", countryCode: "IT", province: "Fermo" },
  { country: "Italy", countryCode: "IT", province: "Ferrara" },
  { country: "Italy", countryCode: "IT", province: "Firenze" },
  { country: "Italy", countryCode: "IT", province: "Foggia" },
  { country: "Italy", countryCode: "IT", province: "Forlì-Cesena" },
  { country: "Italy", countryCode: "IT", province: "Frosinone" },
  { country: "Italy", countryCode: "IT", province: "Genova" },
  { country: "Italy", countryCode: "IT", province: "Gorizia" },
  { country: "Italy", countryCode: "IT", province: "Grosseto" },
  { country: "Italy", countryCode: "IT", province: "Imperia" },
  { country: "Italy", countryCode: "IT", province: "Isernia" },
  { country: "Italy", countryCode: "IT", province: "L’Aquila" },
  { country: "Italy", countryCode: "IT", province: "La Spezia" },
  { country: "Italy", countryCode: "IT", province: "Latina" },
  { country: "Italy", countryCode: "IT", province: "Lecce" },
  { country: "Italy", countryCode: "IT", province: "Lecco" },
  { country: "Italy", countryCode: "IT", province: "Livorno" },
  { country: "Italy", countryCode: "IT", province: "Lodi" },
  { country: "Italy", countryCode: "IT", province: "Lucca" },
  { country: "Italy", countryCode: "IT", province: "Macerata" },
  { country: "Italy", countryCode: "IT", province: "Mantova" },
  { country: "Italy", countryCode: "IT", province: "Massa-Carrara" },
  { country: "Italy", countryCode: "IT", province: "Matera" },
  { country: "Italy", countryCode: "IT", province: "Medio Campidano" },
  { country: "Italy", countryCode: "IT", province: "Messina" },
  { country: "Italy", countryCode: "IT", province: "Milano" },
  { country: "Italy", countryCode: "IT", province: "Modena" },
  { country: "Italy", countryCode: "IT", province: "Monza e Brianza" },
  { country: "Italy", countryCode: "IT", province: "Napoli" },
  { country: "Italy", countryCode: "IT", province: "Novara" },
  { country: "Italy", countryCode: "IT", province: "Nuoro" },
  { country: "Italy", countryCode: "IT", province: "Ogliastra" },
  { country: "Italy", countryCode: "IT", province: "Olbia-Tempio" },
  { country: "Italy", countryCode: "IT", province: "Oristano" },
  { country: "Italy", countryCode: "IT", province: "Padova" },
  { country: "Italy", countryCode: "IT", province: "Palermo" },
  { country: "Italy", countryCode: "IT", province: "Parma" },
  { country: "Italy", countryCode: "IT", province: "Pavia" },
  { country: "Italy", countryCode: "IT", province: "Perugia" },
  { country: "Italy", countryCode: "IT", province: "Pesaro e Urbino" },
  { country: "Italy", countryCode: "IT", province: "Pescara" },
  { country: "Italy", countryCode: "IT", province: "Piacenza" },
  { country: "Italy", countryCode: "IT", province: "Pisa" },
  { country: "Italy", countryCode: "IT", province: "Pistoia" },
  { country: "Italy", countryCode: "IT", province: "Pordenone" },
  { country: "Italy", countryCode: "IT", province: "Potenza" },
  { country: "Italy", countryCode: "IT", province: "Prato" },
  { country: "Italy", countryCode: "IT", province: "Ragusa" },
  { country: "Italy", countryCode: "IT", province: "Ravenna" },
  { country: "Italy", countryCode: "IT", province: "Reggio Calabria" },
  { country: "Italy", countryCode: "IT", province: "Reggio Emilia" },
  { country: "Italy", countryCode: "IT", province: "Rieti" },
  { country: "Italy", countryCode: "IT", province: "Rimini" },
  { country: "Italy", countryCode: "IT", province: "Roma" },
  { country: "Italy", countryCode: "IT", province: "Rovigo" },
  { country: "Italy", countryCode: "IT", province: "Salerno" },
  { country: "Italy", countryCode: "IT", province: "Sassari" },
  { country: "Italy", countryCode: "IT", province: "Savona" },
  { country: "Italy", countryCode: "IT", province: "Siena" },
  { country: "Italy", countryCode: "IT", province: "Siracusa" },
  { country: "Italy", countryCode: "IT", province: "Sondrio" },
  { country: "Italy", countryCode: "IT", province: "Taranto" },
  { country: "Italy", countryCode: "IT", province: "Teramo" },
  { country: "Italy", countryCode: "IT", province: "Terni" },
  { country: "Italy", countryCode: "IT", province: "Torino" },
  { country: "Italy", countryCode: "IT", province: "Trapani" },
  { country: "Italy", countryCode: "IT", province: "Trento" },
  { country: "Italy", countryCode: "IT", province: "Treviso" },
  { country: "Italy", countryCode: "IT", province: "Trieste" },
  { country: "Italy", countryCode: "IT", province: "Udine" },
  { country: "Italy", countryCode: "IT", province: "Varese" },
  { country: "Italy", countryCode: "IT", province: "Venezia" },
  { country: "Italy", countryCode: "IT", province: "Verbano-Cusio-Ossola" },
  { country: "Italy", countryCode: "IT", province: "Vercelli" },
  { country: "Italy", countryCode: "IT", province: "Verona" },
  { country: "Italy", countryCode: "IT", province: "Vibo Valentia" },
  { country: "Italy", countryCode: "IT", province: "Vicenza" },
  { country: "Italy", countryCode: "IT", province: "Viterbo" },
  { country: "Japan", countryCode: "JP", province: "Hokkaidō" },
  { country: "Japan", countryCode: "JP", province: "Aomori" },
  { country: "Japan", countryCode: "JP", province: "Iwate" },
  { country: "Japan", countryCode: "JP", province: "Miyagi" },
  { country: "Japan", countryCode: "JP", province: "Akita" },
  { country: "Japan", countryCode: "JP", province: "Yamagata" },
  { country: "Japan", countryCode: "JP", province: "Fukushima" },
  { country: "Japan", countryCode: "JP", province: "Ibaraki" },
  { country: "Japan", countryCode: "JP", province: "Tochigi" },
  { country: "Japan", countryCode: "JP", province: "Gunma" },
  { country: "Japan", countryCode: "JP", province: "Saitama" },
  { country: "Japan", countryCode: "JP", province: "Chiba" },
  { country: "Japan", countryCode: "JP", province: "Tōkyō" },
  { country: "Japan", countryCode: "JP", province: "Kanagawa" },
  { country: "Japan", countryCode: "JP", province: "Niigata" },
  { country: "Japan", countryCode: "JP", province: "Toyama" },
  { country: "Japan", countryCode: "JP", province: "Ishikawa" },
  { country: "Japan", countryCode: "JP", province: "Fukui" },
  { country: "Japan", countryCode: "JP", province: "Yamanashi" },
  { country: "Japan", countryCode: "JP", province: "Nagano" },
  { country: "Japan", countryCode: "JP", province: "Gifu" },
  { country: "Japan", countryCode: "JP", province: "Shizuoka" },
  { country: "Japan", countryCode: "JP", province: "Aichi" },
  { country: "Japan", countryCode: "JP", province: "Mie" },
  { country: "Japan", countryCode: "JP", province: "Shiga" },
  { country: "Japan", countryCode: "JP", province: "Kyōto" },
  { country: "Japan", countryCode: "JP", province: "Ōsaka" },
  { country: "Japan", countryCode: "JP", province: "Hyōgo" },
  { country: "Japan", countryCode: "JP", province: "Nara" },
  { country: "Japan", countryCode: "JP", province: "Wakayama" },
  { country: "Japan", countryCode: "JP", province: "Tottori" },
  { country: "Japan", countryCode: "JP", province: "Shimane" },
  { country: "Japan", countryCode: "JP", province: "Okayama" },
  { country: "Japan", countryCode: "JP", province: "Hiroshima" },
  { country: "Japan", countryCode: "JP", province: "Yamaguchi" },
  { country: "Japan", countryCode: "JP", province: "Tokushima" },
  { country: "Japan", countryCode: "JP", province: "Kagawa" },
  { country: "Japan", countryCode: "JP", province: "Ehime" },
  { country: "Japan", countryCode: "JP", province: "Kōchi" },
  { country: "Japan", countryCode: "JP", province: "Fukuoka" },
  { country: "Japan", countryCode: "JP", province: "Saga" },
  { country: "Japan", countryCode: "JP", province: "Nagasaki" },
  { country: "Japan", countryCode: "JP", province: "Kumamoto" },
  { country: "Japan", countryCode: "JP", province: "Ōita" },
  { country: "Japan", countryCode: "JP", province: "Miyazaki" },
  { country: "Japan", countryCode: "JP", province: "Kagoshima" },
  { country: "Japan", countryCode: "JP", province: "Okinawa" },
  { country: "Malaysia", countryCode: "MY", province: "Johor" },
  { country: "Malaysia", countryCode: "MY", province: "Kedah" },
  { country: "Malaysia", countryCode: "MY", province: "Kelantan" },
  { country: "Malaysia", countryCode: "MY", province: "Kuala Lumpur" },
  { country: "Malaysia", countryCode: "MY", province: "Labuan" },
  { country: "Malaysia", countryCode: "MY", province: "Melaka" },
  { country: "Malaysia", countryCode: "MY", province: "Negeri Sembilan" },
  { country: "Malaysia", countryCode: "MY", province: "Pahang" },
  { country: "Malaysia", countryCode: "MY", province: "Penang" },
  { country: "Malaysia", countryCode: "MY", province: "Perak" },
  { country: "Malaysia", countryCode: "MY", province: "Perlis" },
  { country: "Malaysia", countryCode: "MY", province: "Putrajaya" },
  { country: "Malaysia", countryCode: "MY", province: "Sabah" },
  { country: "Malaysia", countryCode: "MY", province: "Sarawak" },
  { country: "Malaysia", countryCode: "MY", province: "Selangor" },
  { country: "Malaysia", countryCode: "MY", province: "Terengganu" },
  { country: "Mexico", countryCode: "MX", province: "Aguascalientes" },
  { country: "Mexico", countryCode: "MX", province: "Baja California" },
  { country: "Mexico", countryCode: "MX", province: "Baja California Sur" },
  { country: "Mexico", countryCode: "MX", province: "Campeche" },
  { country: "Mexico", countryCode: "MX", province: "Chiapas" },
  { country: "Mexico", countryCode: "MX", province: "Chihuahua" },
  { country: "Mexico", countryCode: "MX", province: "Ciudad de México" },
  { country: "Mexico", countryCode: "MX", province: "Coahuila" },
  { country: "Mexico", countryCode: "MX", province: "Colima" },
  { country: "Mexico", countryCode: "MX", province: "Durango" },
  { country: "Mexico", countryCode: "MX", province: "Guanajuato" },
  { country: "Mexico", countryCode: "MX", province: "Guerrero" },
  { country: "Mexico", countryCode: "MX", province: "Hidalgo" },
  { country: "Mexico", countryCode: "MX", province: "Jalisco" },
  { country: "Mexico", countryCode: "MX", province: "México" },
  { country: "Mexico", countryCode: "MX", province: "Michoacán" },
  { country: "Mexico", countryCode: "MX", province: "Morelos" },
  { country: "Mexico", countryCode: "MX", province: "Nayarit" },
  { country: "Mexico", countryCode: "MX", province: "Nuevo León" },
  { country: "Mexico", countryCode: "MX", province: "Oaxaca" },
  { country: "Mexico", countryCode: "MX", province: "Puebla" },
  { country: "Mexico", countryCode: "MX", province: "Querétaro" },
  { country: "Mexico", countryCode: "MX", province: "Quintana Roo" },
  { country: "Mexico", countryCode: "MX", province: "San Luis Potosí" },
  { country: "Mexico", countryCode: "MX", province: "Sinaloa" },
  { country: "Mexico", countryCode: "MX", province: "Sonora" },
  { country: "Mexico", countryCode: "MX", province: "Tabasco" },
  { country: "Mexico", countryCode: "MX", province: "Tamaulipas" },
  { country: "Mexico", countryCode: "MX", province: "Tlaxcala" },
  { country: "Mexico", countryCode: "MX", province: "Veracruz" },
  { country: "Mexico", countryCode: "MX", province: "Yucatán" },
  { country: "Mexico", countryCode: "MX", province: "Zacatecas" },
  { country: "New Zealand", countryCode: "NZ", province: "Auckland" },
  { country: "New Zealand", countryCode: "NZ", province: "Bay of Plenty" },
  { country: "New Zealand", countryCode: "NZ", province: "Canterbury" },
  { country: "New Zealand", countryCode: "NZ", province: "Chatham Islands" },
  { country: "New Zealand", countryCode: "NZ", province: "Gisborne" },
  { country: "New Zealand", countryCode: "NZ", province: "Hawke’s Bay" },
  { country: "New Zealand", countryCode: "NZ", province: "Manawatu-Wanganui" },
  { country: "New Zealand", countryCode: "NZ", province: "Marlborough" },
  { country: "New Zealand", countryCode: "NZ", province: "Nelson" },
  { country: "New Zealand", countryCode: "NZ", province: "Northland" },
  { country: "New Zealand", countryCode: "NZ", province: "Otago" },
  { country: "New Zealand", countryCode: "NZ", province: "Southland" },
  { country: "New Zealand", countryCode: "NZ", province: "Taranaki" },
  { country: "New Zealand", countryCode: "NZ", province: "Tasman" },
  { country: "New Zealand", countryCode: "NZ", province: "Waikato" },
  { country: "New Zealand", countryCode: "NZ", province: "Wellington" },
  { country: "New Zealand", countryCode: "NZ", province: "West Coast" },
  { country: "Nigeria", countryCode: "NG", province: "Abia" },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Abuja Federal Capital Territory",
  },
  { country: "Nigeria", countryCode: "NG", province: "Adamawa" },
  { country: "Nigeria", countryCode: "NG", province: "Akwa Ibom" },
  { country: "Nigeria", countryCode: "NG", province: "Anambra" },
  { country: "Nigeria", countryCode: "NG", province: "Bauchi" },
  { country: "Nigeria", countryCode: "NG", province: "Bayelsa" },
  { country: "Nigeria", countryCode: "NG", province: "Benue" },
  { country: "Nigeria", countryCode: "NG", province: "Borno" },
  { country: "Nigeria", countryCode: "NG", province: "Cross River" },
  { country: "Nigeria", countryCode: "NG", province: "Delta" },
  { country: "Nigeria", countryCode: "NG", province: "Ebonyi" },
  { country: "Nigeria", countryCode: "NG", province: "Edo" },
  { country: "Nigeria", countryCode: "NG", province: "Ekiti" },
  { country: "Nigeria", countryCode: "NG", province: "Enugu" },
  { country: "Nigeria", countryCode: "NG", province: "Gombe" },
  { country: "Nigeria", countryCode: "NG", province: "Imo" },
  { country: "Nigeria", countryCode: "NG", province: "Jigawa" },
  { country: "Nigeria", countryCode: "NG", province: "Kaduna" },
  { country: "Nigeria", countryCode: "NG", province: "Kano" },
  { country: "Nigeria", countryCode: "NG", province: "Katsina" },
  { country: "Nigeria", countryCode: "NG", province: "Kebbi" },
  { country: "Nigeria", countryCode: "NG", province: "Kogi" },
  { country: "Nigeria", countryCode: "NG", province: "Kwara" },
  { country: "Nigeria", countryCode: "NG", province: "Lagos" },
  { country: "Nigeria", countryCode: "NG", province: "Nasarawa" },
  { country: "Nigeria", countryCode: "NG", province: "Niger" },
  { country: "Nigeria", countryCode: "NG", province: "Ogun" },
  { country: "Nigeria", countryCode: "NG", province: "Ondo" },
  { country: "Nigeria", countryCode: "NG", province: "Osun" },
  { country: "Nigeria", countryCode: "NG", province: "Oyo" },
  { country: "Nigeria", countryCode: "NG", province: "Plateau" },
  { country: "Nigeria", countryCode: "NG", province: "Rivers" },
  { country: "Nigeria", countryCode: "NG", province: "Sokoto" },
  { country: "Nigeria", countryCode: "NG", province: "Taraba" },
  { country: "Nigeria", countryCode: "NG", province: "Yobe" },
  { country: "Nigeria", countryCode: "NG", province: "Zamfara" },
  { country: "Panama", countryCode: "PA", province: "Bocas del Toro" },
  { country: "Panama", countryCode: "PA", province: "Chiriquí" },
  { country: "Panama", countryCode: "PA", province: "Coclé" },
  { country: "Panama", countryCode: "PA", province: "Colón" },
  { country: "Panama", countryCode: "PA", province: "Darién" },
  { country: "Panama", countryCode: "PA", province: "Emberá" },
  { country: "Panama", countryCode: "PA", province: "Herrera" },
  { country: "Panama", countryCode: "PA", province: "Kuna Yala" },
  { country: "Panama", countryCode: "PA", province: "Los Santos" },
  { country: "Panama", countryCode: "PA", province: "Ngöbe-Buglé" },
  { country: "Panama", countryCode: "PA", province: "Panamá" },
  { country: "Panama", countryCode: "PA", province: "Panamá Oeste" },
  { country: "Panama", countryCode: "PA", province: "Veraguas" },
  { country: "Peru", countryCode: "PE", province: "Amazonas" },
  { country: "Peru", countryCode: "PE", province: "Áncash" },
  { country: "Peru", countryCode: "PE", province: "Apurímac" },
  { country: "Peru", countryCode: "PE", province: "Arequipa" },
  { country: "Peru", countryCode: "PE", province: "Ayacucho" },
  { country: "Peru", countryCode: "PE", province: "Cajamarca" },
  { country: "Peru", countryCode: "PE", province: "Callao" },
  { country: "Peru", countryCode: "PE", province: "Cuzco" },
  { country: "Peru", countryCode: "PE", province: "Huancavelica" },
  { country: "Peru", countryCode: "PE", province: "Huánuco" },
  { country: "Peru", countryCode: "PE", province: "Ica" },
  { country: "Peru", countryCode: "PE", province: "Junín" },
  { country: "Peru", countryCode: "PE", province: "La Libertad" },
  { country: "Peru", countryCode: "PE", province: "Lambayeque" },
  { country: "Peru", countryCode: "PE", province: "Lima (departamento)" },
  { country: "Peru", countryCode: "PE", province: "Lima (provincia)" },
  { country: "Peru", countryCode: "PE", province: "Loreto" },
  { country: "Peru", countryCode: "PE", province: "Madre de Dios" },
  { country: "Peru", countryCode: "PE", province: "Moquegua" },
  { country: "Peru", countryCode: "PE", province: "Pasco" },
  { country: "Peru", countryCode: "PE", province: "Piura" },
  { country: "Peru", countryCode: "PE", province: "Puno" },
  { country: "Peru", countryCode: "PE", province: "San Martín" },
  { country: "Peru", countryCode: "PE", province: "Tacna" },
  { country: "Peru", countryCode: "PE", province: "Tumbes" },
  { country: "Peru", countryCode: "PE", province: "Ucayali" },
  { country: "Philippines", countryCode: "PH", province: "Abra" },
  { country: "Philippines", countryCode: "PH", province: "Agusan del Norte" },
  { country: "Philippines", countryCode: "PH", province: "Agusan del Sur" },
  { country: "Philippines", countryCode: "PH", province: "Aklan" },
  { country: "Philippines", countryCode: "PH", province: "Albay" },
  { country: "Philippines", countryCode: "PH", province: "Antique" },
  { country: "Philippines", countryCode: "PH", province: "Apayao" },
  { country: "Philippines", countryCode: "PH", province: "Aurora" },
  { country: "Philippines", countryCode: "PH", province: "Basilan" },
  { country: "Philippines", countryCode: "PH", province: "Bataan" },
  { country: "Philippines", countryCode: "PH", province: "Batanes" },
  { country: "Philippines", countryCode: "PH", province: "Batangas" },
  { country: "Philippines", countryCode: "PH", province: "Benguet" },
  { country: "Philippines", countryCode: "PH", province: "Biliran" },
  { country: "Philippines", countryCode: "PH", province: "Bohol" },
  { country: "Philippines", countryCode: "PH", province: "Bukidnon" },
  { country: "Philippines", countryCode: "PH", province: "Bulacan" },
  { country: "Philippines", countryCode: "PH", province: "Cagayan" },
  { country: "Philippines", countryCode: "PH", province: "Camarines Norte" },
  { country: "Philippines", countryCode: "PH", province: "Camarines Sur" },
  { country: "Philippines", countryCode: "PH", province: "Camiguin" },
  { country: "Philippines", countryCode: "PH", province: "Capiz" },
  { country: "Philippines", countryCode: "PH", province: "Catanduanes" },
  { country: "Philippines", countryCode: "PH", province: "Cavite" },
  { country: "Philippines", countryCode: "PH", province: "Cebu" },
  { country: "Philippines", countryCode: "PH", province: "Cotabato" },
  { country: "Philippines", countryCode: "PH", province: "Davao de Oro" },
  { country: "Philippines", countryCode: "PH", province: "Davao del Norte" },
  { country: "Philippines", countryCode: "PH", province: "Davao del Sur" },
  { country: "Philippines", countryCode: "PH", province: "Davao Occidental" },
  { country: "Philippines", countryCode: "PH", province: "Davao Oriental" },
  { country: "Philippines", countryCode: "PH", province: "Dinagat Islands" },
  { country: "Philippines", countryCode: "PH", province: "Eastern Samar" },
  { country: "Philippines", countryCode: "PH", province: "Guimaras" },
  { country: "Philippines", countryCode: "PH", province: "Ifugao" },
  { country: "Philippines", countryCode: "PH", province: "Ilocos Norte" },
  { country: "Philippines", countryCode: "PH", province: "Ilocos Sur" },
  { country: "Philippines", countryCode: "PH", province: "Iloilo" },
  { country: "Philippines", countryCode: "PH", province: "Isabela" },
  { country: "Philippines", countryCode: "PH", province: "Kalinga" },
  { country: "Philippines", countryCode: "PH", province: "La Union" },
  { country: "Philippines", countryCode: "PH", province: "Laguna" },
  { country: "Philippines", countryCode: "PH", province: "Lanao del Norte" },
  { country: "Philippines", countryCode: "PH", province: "Lanao del Sur" },
  { country: "Philippines", countryCode: "PH", province: "Leyte" },
  { country: "Philippines", countryCode: "PH", province: "Maguindanao" },
  { country: "Philippines", countryCode: "PH", province: "Marinduque" },
  { country: "Philippines", countryCode: "PH", province: "Masbate" },
  { country: "Philippines", countryCode: "PH", province: "Metro Manila" },
  { country: "Philippines", countryCode: "PH", province: "Misamis Occidental" },
  { country: "Philippines", countryCode: "PH", province: "Misamis Oriental" },
  { country: "Philippines", countryCode: "PH", province: "Mountain Province" },
  { country: "Philippines", countryCode: "PH", province: "Negros Occidental" },
  { country: "Philippines", countryCode: "PH", province: "Negros Oriental" },
  { country: "Philippines", countryCode: "PH", province: "Northern Samar" },
  { country: "Philippines", countryCode: "PH", province: "Nueva Ecija" },
  { country: "Philippines", countryCode: "PH", province: "Nueva Vizcaya" },
  { country: "Philippines", countryCode: "PH", province: "Occidental Mindoro" },
  { country: "Philippines", countryCode: "PH", province: "Oriental Mindoro" },
  { country: "Philippines", countryCode: "PH", province: "Palawan" },
  { country: "Philippines", countryCode: "PH", province: "Pampanga" },
  { country: "Philippines", countryCode: "PH", province: "Pangasinan" },
  { country: "Philippines", countryCode: "PH", province: "Quezon" },
  { country: "Philippines", countryCode: "PH", province: "Quirino" },
  { country: "Philippines", countryCode: "PH", province: "Rizal" },
  { country: "Philippines", countryCode: "PH", province: "Romblon" },
  { country: "Philippines", countryCode: "PH", province: "Samar" },
  { country: "Philippines", countryCode: "PH", province: "Sarangani" },
  { country: "Philippines", countryCode: "PH", province: "Siquijor" },
  { country: "Philippines", countryCode: "PH", province: "Sorsogon" },
  { country: "Philippines", countryCode: "PH", province: "South Cotabato" },
  { country: "Philippines", countryCode: "PH", province: "Southern Leyte" },
  { country: "Philippines", countryCode: "PH", province: "Sultan Kudarat" },
  { country: "Philippines", countryCode: "PH", province: "Sulu" },
  { country: "Philippines", countryCode: "PH", province: "Surigao del Norte" },
  { country: "Philippines", countryCode: "PH", province: "Surigao del Sur" },
  { country: "Philippines", countryCode: "PH", province: "Tarlac" },
  { country: "Philippines", countryCode: "PH", province: "Tawi-Tawi" },
  { country: "Philippines", countryCode: "PH", province: "Zambales" },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Zamboanga del Norte",
  },
  { country: "Philippines", countryCode: "PH", province: "Zamboanga del Sur" },
  { country: "Philippines", countryCode: "PH", province: "Zamboanga Sibugay" },
  { country: "Portugal", countryCode: "PT", province: "Açores" },
  { country: "Portugal", countryCode: "PT", province: "Aveiro" },
  { country: "Portugal", countryCode: "PT", province: "Beja" },
  { country: "Portugal", countryCode: "PT", province: "Braga" },
  { country: "Portugal", countryCode: "PT", province: "Bragança" },
  { country: "Portugal", countryCode: "PT", province: "Castelo Branco" },
  { country: "Portugal", countryCode: "PT", province: "Coimbra" },
  { country: "Portugal", countryCode: "PT", province: "Évora" },
  { country: "Portugal", countryCode: "PT", province: "Faro" },
  { country: "Portugal", countryCode: "PT", province: "Guarda" },
  { country: "Portugal", countryCode: "PT", province: "Leiria" },
  { country: "Portugal", countryCode: "PT", province: "Lisboa" },
  { country: "Portugal", countryCode: "PT", province: "Madeira" },
  { country: "Portugal", countryCode: "PT", province: "Portalegre" },
  { country: "Portugal", countryCode: "PT", province: "Porto" },
  { country: "Portugal", countryCode: "PT", province: "Santarém" },
  { country: "Portugal", countryCode: "PT", province: "Setúbal" },
  { country: "Portugal", countryCode: "PT", province: "Viana do Castelo" },
  { country: "Portugal", countryCode: "PT", province: "Vila Real" },
  { country: "Portugal", countryCode: "PT", province: "Viseu" },
  { country: "Romania", countryCode: "RO", province: "Alba" },
  { country: "Romania", countryCode: "RO", province: "Arad" },
  { country: "Romania", countryCode: "RO", province: "Argeș" },
  { country: "Romania", countryCode: "RO", province: "Bacău" },
  { country: "Romania", countryCode: "RO", province: "Bihor" },
  { country: "Romania", countryCode: "RO", province: "Bistrița-Năsăud" },
  { country: "Romania", countryCode: "RO", province: "Botoșani" },
  { country: "Romania", countryCode: "RO", province: "Brașov" },
  { country: "Romania", countryCode: "RO", province: "Brăila" },
  { country: "Romania", countryCode: "RO", province: "București" },
  { country: "Romania", countryCode: "RO", province: "Buzău" },
  { country: "Romania", countryCode: "RO", province: "Călărași" },
  { country: "Romania", countryCode: "RO", province: "Caraș-Severin" },
  { country: "Romania", countryCode: "RO", province: "Cluj" },
  { country: "Romania", countryCode: "RO", province: "Constanța" },
  { country: "Romania", countryCode: "RO", province: "Covasna" },
  { country: "Romania", countryCode: "RO", province: "Dâmbovița" },
  { country: "Romania", countryCode: "RO", province: "Dolj" },
  { country: "Romania", countryCode: "RO", province: "Galați" },
  { country: "Romania", countryCode: "RO", province: "Giurgiu" },
  { country: "Romania", countryCode: "RO", province: "Gorj" },
  { country: "Romania", countryCode: "RO", province: "Harghita" },
  { country: "Romania", countryCode: "RO", province: "Hunedoara" },
  { country: "Romania", countryCode: "RO", province: "Iași" },
  { country: "Romania", countryCode: "RO", province: "Ialomița" },
  { country: "Romania", countryCode: "RO", province: "Ilfov" },
  { country: "Romania", countryCode: "RO", province: "Maramureș" },
  { country: "Romania", countryCode: "RO", province: "Mehedinți" },
  { country: "Romania", countryCode: "RO", province: "Mureș" },
  { country: "Romania", countryCode: "RO", province: "Neamț" },
  { country: "Romania", countryCode: "RO", province: "Olt" },
  { country: "Romania", countryCode: "RO", province: "Prahova" },
  { country: "Romania", countryCode: "RO", province: "Sălaj" },
  { country: "Romania", countryCode: "RO", province: "Satu Mare" },
  { country: "Romania", countryCode: "RO", province: "Sibiu" },
  { country: "Romania", countryCode: "RO", province: "Suceava" },
  { country: "Romania", countryCode: "RO", province: "Teleorman" },
  { country: "Romania", countryCode: "RO", province: "Timiș" },
  { country: "Romania", countryCode: "RO", province: "Tulcea" },
  { country: "Romania", countryCode: "RO", province: "Vâlcea" },
  { country: "Romania", countryCode: "RO", province: "Vaslui" },
  { country: "Romania", countryCode: "RO", province: "Vrancea" },
  { country: "Russia", countryCode: "RU", province: "Republic of Adygeya" },
  { country: "Russia", countryCode: "RU", province: "Altai Republic" },
  { country: "Russia", countryCode: "RU", province: "Altai Krai" },
  { country: "Russia", countryCode: "RU", province: "Amur Oblast" },
  { country: "Russia", countryCode: "RU", province: "Arkhangelsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Astrakhan Oblast" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Bashkortostan",
  },
  { country: "Russia", countryCode: "RU", province: "Belgorod Oblast" },
  { country: "Russia", countryCode: "RU", province: "Bryansk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Republic of Buryatia" },
  { country: "Russia", countryCode: "RU", province: "Chechen Republic" },
  { country: "Russia", countryCode: "RU", province: "Chelyabinsk Oblast" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Chukotka Autonomous Okrug",
  },
  { country: "Russia", countryCode: "RU", province: "Chuvash Republic" },
  { country: "Russia", countryCode: "RU", province: "Republic of Dagestan" },
  { country: "Russia", countryCode: "RU", province: "Republic of Ingushetia" },
  { country: "Russia", countryCode: "RU", province: "Irkutsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Ivanovo Oblast" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Jewish Autonomous Oblast",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kabardino-Balkarian Republic",
  },
  { country: "Russia", countryCode: "RU", province: "Kaliningrad Oblast" },
  { country: "Russia", countryCode: "RU", province: "Republic of Kalmykia" },
  { country: "Russia", countryCode: "RU", province: "Kaluga Oblast" },
  { country: "Russia", countryCode: "RU", province: "Kamchatka Krai" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Karachay–Cherkess Republic",
  },
  { country: "Russia", countryCode: "RU", province: "Republic of Karelia" },
  { country: "Russia", countryCode: "RU", province: "Kemerovo Oblast" },
  { country: "Russia", countryCode: "RU", province: "Khabarovsk Krai" },
  { country: "Russia", countryCode: "RU", province: "Republic of Khakassia" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Khanty-Mansi Autonomous Okrug",
  },
  { country: "Russia", countryCode: "RU", province: "Kirov Oblast" },
  { country: "Russia", countryCode: "RU", province: "Komi Republic" },
  { country: "Russia", countryCode: "RU", province: "Kostroma Oblast" },
  { country: "Russia", countryCode: "RU", province: "Krasnodar Krai" },
  { country: "Russia", countryCode: "RU", province: "Krasnoyarsk Krai" },
  { country: "Russia", countryCode: "RU", province: "Kurgan Oblast" },
  { country: "Russia", countryCode: "RU", province: "Kursk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Leningrad Oblast" },
  { country: "Russia", countryCode: "RU", province: "Lipetsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Magadan Oblast" },
  { country: "Russia", countryCode: "RU", province: "Mari El Republic" },
  { country: "Russia", countryCode: "RU", province: "Republic of Mordovia" },
  { country: "Russia", countryCode: "RU", province: "Moscow" },
  { country: "Russia", countryCode: "RU", province: "Moscow Oblast" },
  { country: "Russia", countryCode: "RU", province: "Murmansk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Nizhny Novgorod Oblast" },
  { country: "Russia", countryCode: "RU", province: "Novgorod Oblast" },
  { country: "Russia", countryCode: "RU", province: "Novosibirsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Omsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Orenburg Oblast" },
  { country: "Russia", countryCode: "RU", province: "Oryol Oblast" },
  { country: "Russia", countryCode: "RU", province: "Penza Oblast" },
  { country: "Russia", countryCode: "RU", province: "Perm Krai" },
  { country: "Russia", countryCode: "RU", province: "Primorsky Krai" },
  { country: "Russia", countryCode: "RU", province: "Pskov Oblast" },
  { country: "Russia", countryCode: "RU", province: "Rostov Oblast" },
  { country: "Russia", countryCode: "RU", province: "Ryazan Oblast" },
  { country: "Russia", countryCode: "RU", province: "Saint Petersburg" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Sakha Republic (Yakutia)",
  },
  { country: "Russia", countryCode: "RU", province: "Sakhalin Oblast" },
  { country: "Russia", countryCode: "RU", province: "Samara Oblast" },
  { country: "Russia", countryCode: "RU", province: "Saratov Oblast" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of North Ossetia–Alania",
  },
  { country: "Russia", countryCode: "RU", province: "Smolensk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Stavropol Krai" },
  { country: "Russia", countryCode: "RU", province: "Sverdlovsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Tambov Oblast" },
  { country: "Russia", countryCode: "RU", province: "Republic of Tatarstan" },
  { country: "Russia", countryCode: "RU", province: "Tomsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Tula Oblast" },
  { country: "Russia", countryCode: "RU", province: "Tver Oblast" },
  { country: "Russia", countryCode: "RU", province: "Tyumen Oblast" },
  { country: "Russia", countryCode: "RU", province: "Tyva Republic" },
  { country: "Russia", countryCode: "RU", province: "Udmurtia" },
  { country: "Russia", countryCode: "RU", province: "Ulyanovsk Oblast" },
  { country: "Russia", countryCode: "RU", province: "Vladimir Oblast" },
  { country: "Russia", countryCode: "RU", province: "Volgograd Oblast" },
  { country: "Russia", countryCode: "RU", province: "Vologda Oblast" },
  { country: "Russia", countryCode: "RU", province: "Voronezh Oblast" },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Yamalo-Nenets Autonomous Okrug",
  },
  { country: "Russia", countryCode: "RU", province: "Yaroslavl Oblast" },
  { country: "Russia", countryCode: "RU", province: "Zabaykalsky Krai" },
  { country: "South Africa", countryCode: "ZA", province: "Eastern Cape" },
  { country: "South Africa", countryCode: "ZA", province: "Free State" },
  { country: "South Africa", countryCode: "ZA", province: "Gauteng" },
  { country: "South Africa", countryCode: "ZA", province: "KwaZulu-Natal" },
  { country: "South Africa", countryCode: "ZA", province: "Limpopo" },
  { country: "South Africa", countryCode: "ZA", province: "Mpumalanga" },
  { country: "South Africa", countryCode: "ZA", province: "North West" },
  { country: "South Africa", countryCode: "ZA", province: "Northern Cape" },
  { country: "South Africa", countryCode: "ZA", province: "Western Cape" },
  { country: "South Korea", countryCode: "KR", province: "Busan" },
  { country: "South Korea", countryCode: "KR", province: "Chungbuk" },
  { country: "South Korea", countryCode: "KR", province: "Chungnam" },
  { country: "South Korea", countryCode: "KR", province: "Daegu" },
  { country: "South Korea", countryCode: "KR", province: "Daejeon" },
  { country: "South Korea", countryCode: "KR", province: "Gangwon" },
  { country: "South Korea", countryCode: "KR", province: "Gwangju" },
  { country: "South Korea", countryCode: "KR", province: "Gyeongbuk" },
  { country: "South Korea", countryCode: "KR", province: "Gyeonggi" },
  { country: "South Korea", countryCode: "KR", province: "Gyeongnam" },
  { country: "South Korea", countryCode: "KR", province: "Incheon" },
  { country: "South Korea", countryCode: "KR", province: "Jeju" },
  { country: "South Korea", countryCode: "KR", province: "Jeonbuk" },
  { country: "South Korea", countryCode: "KR", province: "Jeonnam" },
  { country: "South Korea", countryCode: "KR", province: "Sejong" },
  { country: "South Korea", countryCode: "KR", province: "Seoul" },
  { country: "South Korea", countryCode: "KR", province: "Ulsan" },
  { country: "Spain", countryCode: "ES", province: "A Coruña" },
  { country: "Spain", countryCode: "ES", province: "Álava" },
  { country: "Spain", countryCode: "ES", province: "Albacete" },
  { country: "Spain", countryCode: "ES", province: "Alicante" },
  { country: "Spain", countryCode: "ES", province: "Almería" },
  { country: "Spain", countryCode: "ES", province: "Asturias" },
  { country: "Spain", countryCode: "ES", province: "Ávila" },
  { country: "Spain", countryCode: "ES", province: "Badajoz" },
  { country: "Spain", countryCode: "ES", province: "Balears" },
  { country: "Spain", countryCode: "ES", province: "Barcelona" },
  { country: "Spain", countryCode: "ES", province: "Burgos" },
  { country: "Spain", countryCode: "ES", province: "Cáceres" },
  { country: "Spain", countryCode: "ES", province: "Cádiz" },
  { country: "Spain", countryCode: "ES", province: "Cantabria" },
  { country: "Spain", countryCode: "ES", province: "Castellón" },
  { country: "Spain", countryCode: "ES", province: "Ceuta" },
  { country: "Spain", countryCode: "ES", province: "Ciudad Real" },
  { country: "Spain", countryCode: "ES", province: "Córdoba" },
  { country: "Spain", countryCode: "ES", province: "Cuenca" },
  { country: "Spain", countryCode: "ES", province: "Girona" },
  { country: "Spain", countryCode: "ES", province: "Granada" },
  { country: "Spain", countryCode: "ES", province: "Guadalajara" },
  { country: "Spain", countryCode: "ES", province: "Guipúzcoa" },
  { country: "Spain", countryCode: "ES", province: "Huelva" },
  { country: "Spain", countryCode: "ES", province: "Huesca" },
  { country: "Spain", countryCode: "ES", province: "Jaén" },
  { country: "Spain", countryCode: "ES", province: "La Rioja" },
  { country: "Spain", countryCode: "ES", province: "Las Palmas" },
  { country: "Spain", countryCode: "ES", province: "León" },
  { country: "Spain", countryCode: "ES", province: "Lleida" },
  { country: "Spain", countryCode: "ES", province: "Lugo" },
  { country: "Spain", countryCode: "ES", province: "Madrid" },
  { country: "Spain", countryCode: "ES", province: "Málaga" },
  { country: "Spain", countryCode: "ES", province: "Melilla" },
  { country: "Spain", countryCode: "ES", province: "Murcia" },
  { country: "Spain", countryCode: "ES", province: "Navarra" },
  { country: "Spain", countryCode: "ES", province: "Ourense" },
  { country: "Spain", countryCode: "ES", province: "Palencia" },
  { country: "Spain", countryCode: "ES", province: "Pontevedra" },
  { country: "Spain", countryCode: "ES", province: "Salamanca" },
  { country: "Spain", countryCode: "ES", province: "Santa Cruz de Tenerife" },
  { country: "Spain", countryCode: "ES", province: "Segovia" },
  { country: "Spain", countryCode: "ES", province: "Sevilla" },
  { country: "Spain", countryCode: "ES", province: "Soria" },
  { country: "Spain", countryCode: "ES", province: "Tarragona" },
  { country: "Spain", countryCode: "ES", province: "Teruel" },
  { country: "Spain", countryCode: "ES", province: "Toledo" },
  { country: "Spain", countryCode: "ES", province: "Valencia" },
  { country: "Spain", countryCode: "ES", province: "Valladolid" },
  { country: "Spain", countryCode: "ES", province: "Vizcaya" },
  { country: "Spain", countryCode: "ES", province: "Zamora" },
  { country: "Spain", countryCode: "ES", province: "Zaragoza" },
  { country: "Thailand", countryCode: "TH", province: "Amnat Charoen" },
  { country: "Thailand", countryCode: "TH", province: "Ang Thong" },
  { country: "Thailand", countryCode: "TH", province: "Bangkok" },
  { country: "Thailand", countryCode: "TH", province: "Bueng Kan" },
  { country: "Thailand", countryCode: "TH", province: "Buriram" },
  { country: "Thailand", countryCode: "TH", province: "Chachoengsao" },
  { country: "Thailand", countryCode: "TH", province: "Chai Nat" },
  { country: "Thailand", countryCode: "TH", province: "Chaiyaphum" },
  { country: "Thailand", countryCode: "TH", province: "Chanthaburi" },
  { country: "Thailand", countryCode: "TH", province: "Chiang Mai" },
  { country: "Thailand", countryCode: "TH", province: "Chiang Rai" },
  { country: "Thailand", countryCode: "TH", province: "Chon Buri" },
  { country: "Thailand", countryCode: "TH", province: "Chumphon" },
  { country: "Thailand", countryCode: "TH", province: "Kalasin" },
  { country: "Thailand", countryCode: "TH", province: "Kamphaeng Phet" },
  { country: "Thailand", countryCode: "TH", province: "Kanchanaburi" },
  { country: "Thailand", countryCode: "TH", province: "Khon Kaen" },
  { country: "Thailand", countryCode: "TH", province: "Krabi" },
  { country: "Thailand", countryCode: "TH", province: "Lampang" },
  { country: "Thailand", countryCode: "TH", province: "Lamphun" },
  { country: "Thailand", countryCode: "TH", province: "Loei" },
  { country: "Thailand", countryCode: "TH", province: "Lopburi" },
  { country: "Thailand", countryCode: "TH", province: "Mae Hong Son" },
  { country: "Thailand", countryCode: "TH", province: "Maha Sarakham" },
  { country: "Thailand", countryCode: "TH", province: "Mukdahan" },
  { country: "Thailand", countryCode: "TH", province: "Nakhon Nayok" },
  { country: "Thailand", countryCode: "TH", province: "Nakhon Pathom" },
  { country: "Thailand", countryCode: "TH", province: "Nakhon Phanom" },
  { country: "Thailand", countryCode: "TH", province: "Nakhon Ratchasima" },
  { country: "Thailand", countryCode: "TH", province: "Nakhon Sawan" },
  { country: "Thailand", countryCode: "TH", province: "Nakhon Si Thammarat" },
  { country: "Thailand", countryCode: "TH", province: "Nan" },
  { country: "Thailand", countryCode: "TH", province: "Narathiwat" },
  { country: "Thailand", countryCode: "TH", province: "Nong Bua Lam Phu" },
  { country: "Thailand", countryCode: "TH", province: "Nong Khai" },
  { country: "Thailand", countryCode: "TH", province: "Nonthaburi" },
  { country: "Thailand", countryCode: "TH", province: "Pathum Thani" },
  { country: "Thailand", countryCode: "TH", province: "Pattani" },
  { country: "Thailand", countryCode: "TH", province: "Pattaya" },
  { country: "Thailand", countryCode: "TH", province: "Phangnga" },
  { country: "Thailand", countryCode: "TH", province: "Phatthalung" },
  { country: "Thailand", countryCode: "TH", province: "Phayao" },
  { country: "Thailand", countryCode: "TH", province: "Phetchabun" },
  { country: "Thailand", countryCode: "TH", province: "Phetchaburi" },
  { country: "Thailand", countryCode: "TH", province: "Phichit" },
  { country: "Thailand", countryCode: "TH", province: "Phitsanulok" },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phra Nakhon Si Ayutthaya",
  },
  { country: "Thailand", countryCode: "TH", province: "Phrae" },
  { country: "Thailand", countryCode: "TH", province: "Phuket" },
  { country: "Thailand", countryCode: "TH", province: "Prachin Buri" },
  { country: "Thailand", countryCode: "TH", province: "Prachuap Khiri Khan" },
  { country: "Thailand", countryCode: "TH", province: "Ranong" },
  { country: "Thailand", countryCode: "TH", province: "Ratchaburi" },
  { country: "Thailand", countryCode: "TH", province: "Rayong" },
  { country: "Thailand", countryCode: "TH", province: "Roi Et" },
  { country: "Thailand", countryCode: "TH", province: "Sa Kaeo" },
  { country: "Thailand", countryCode: "TH", province: "Sakon Nakhon" },
  { country: "Thailand", countryCode: "TH", province: "Samut Prakan" },
  { country: "Thailand", countryCode: "TH", province: "Samut Sakhon" },
  { country: "Thailand", countryCode: "TH", province: "Samut Songkhram" },
  { country: "Thailand", countryCode: "TH", province: "Saraburi" },
  { country: "Thailand", countryCode: "TH", province: "Satun" },
  { country: "Thailand", countryCode: "TH", province: "Sing Buri" },
  { country: "Thailand", countryCode: "TH", province: "Sisaket" },
  { country: "Thailand", countryCode: "TH", province: "Songkhla" },
  { country: "Thailand", countryCode: "TH", province: "Sukhothai" },
  { country: "Thailand", countryCode: "TH", province: "Suphan Buri" },
  { country: "Thailand", countryCode: "TH", province: "Surat Thani" },
  { country: "Thailand", countryCode: "TH", province: "Surin" },
  { country: "Thailand", countryCode: "TH", province: "Tak" },
  { country: "Thailand", countryCode: "TH", province: "Trang" },
  { country: "Thailand", countryCode: "TH", province: "Trat" },
  { country: "Thailand", countryCode: "TH", province: "Ubon Ratchathani" },
  { country: "Thailand", countryCode: "TH", province: "Udon Thani" },
  { country: "Thailand", countryCode: "TH", province: "Uthai Thani" },
  { country: "Thailand", countryCode: "TH", province: "Uttaradit" },
  { country: "Thailand", countryCode: "TH", province: "Yala" },
  { country: "Thailand", countryCode: "TH", province: "Yasothon" },
  { country: "United Arab Emirates", countryCode: "AE", province: "Abu Dhabi" },
  { country: "United Arab Emirates", countryCode: "AE", province: "Ajman" },
  { country: "United Arab Emirates", countryCode: "AE", province: "Dubai" },
  { country: "United Arab Emirates", countryCode: "AE", province: "Fujairah" },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Ras al-Khaimah",
  },
  { country: "United Arab Emirates", countryCode: "AE", province: "Sharjah" },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Umm al-Quwain",
  },
  { country: "United Kingdom", countryCode: "GB", province: "British Forces" },
  { country: "United Kingdom", countryCode: "GB", province: "England" },
  {
    country: "United Kingdom",
    countryCode: "GB",
    province: "Northern Ireland",
  },
  { country: "United Kingdom", countryCode: "GB", province: "Scotland" },
  { country: "United Kingdom", countryCode: "GB", province: "Wales" },
  { country: "United States", countryCode: "US", province: "Alabama" },
  { country: "United States", countryCode: "US", province: "Alaska" },
  { country: "United States", countryCode: "US", province: "American Samoa" },
  { country: "United States", countryCode: "US", province: "Arizona" },
  { country: "United States", countryCode: "US", province: "Arkansas" },
  { country: "United States", countryCode: "US", province: "California" },
  { country: "United States", countryCode: "US", province: "Colorado" },
  { country: "United States", countryCode: "US", province: "Connecticut" },
  { country: "United States", countryCode: "US", province: "Delaware" },
  {
    country: "United States",
    countryCode: "US",
    province: "District of Columbia",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Federated States of Micronesia",
  },
  { country: "United States", countryCode: "US", province: "Florida" },
  { country: "United States", countryCode: "US", province: "Georgia" },
  { country: "United States", countryCode: "US", province: "Guam" },
  { country: "United States", countryCode: "US", province: "Hawaii" },
  { country: "United States", countryCode: "US", province: "Idaho" },
  { country: "United States", countryCode: "US", province: "Illinois" },
  { country: "United States", countryCode: "US", province: "Indiana" },
  { country: "United States", countryCode: "US", province: "Iowa" },
  { country: "United States", countryCode: "US", province: "Kansas" },
  { country: "United States", countryCode: "US", province: "Kentucky" },
  { country: "United States", countryCode: "US", province: "Louisiana" },
  { country: "United States", countryCode: "US", province: "Maine" },
  { country: "United States", countryCode: "US", province: "Marshall Islands" },
  { country: "United States", countryCode: "US", province: "Maryland" },
  { country: "United States", countryCode: "US", province: "Massachusetts" },
  { country: "United States", countryCode: "US", province: "Michigan" },
  { country: "United States", countryCode: "US", province: "Minnesota" },
  { country: "United States", countryCode: "US", province: "Mississippi" },
  { country: "United States", countryCode: "US", province: "Missouri" },
  { country: "United States", countryCode: "US", province: "Montana" },
  { country: "United States", countryCode: "US", province: "Nebraska" },
  { country: "United States", countryCode: "US", province: "Nevada" },
  { country: "United States", countryCode: "US", province: "New Hampshire" },
  { country: "United States", countryCode: "US", province: "New Jersey" },
  { country: "United States", countryCode: "US", province: "New Mexico" },
  { country: "United States", countryCode: "US", province: "New York" },
  { country: "United States", countryCode: "US", province: "North Carolina" },
  { country: "United States", countryCode: "US", province: "North Dakota" },
  {
    country: "United States",
    countryCode: "US",
    province: "Northern Mariana Islands",
  },
  { country: "United States", countryCode: "US", province: "Ohio" },
  { country: "United States", countryCode: "US", province: "Oklahoma" },
  { country: "United States", countryCode: "US", province: "Oregon" },
  { country: "United States", countryCode: "US", province: "Palau" },
  { country: "United States", countryCode: "US", province: "Pennsylvania" },
  { country: "United States", countryCode: "US", province: "Puerto Rico" },
  { country: "United States", countryCode: "US", province: "Rhode Island" },
  { country: "United States", countryCode: "US", province: "South Carolina" },
  { country: "United States", countryCode: "US", province: "South Dakota" },
  { country: "United States", countryCode: "US", province: "Tennessee" },
  { country: "United States", countryCode: "US", province: "Texas" },
  { country: "United States", countryCode: "US", province: "Utah" },
  { country: "United States", countryCode: "US", province: "Vermont" },
  { country: "United States", countryCode: "US", province: "Virginia" },
  { country: "United States", countryCode: "US", province: "Washington" },
  { country: "United States", countryCode: "US", province: "West Virginia" },
  { country: "United States", countryCode: "US", province: "Wisconsin" },
  { country: "United States", countryCode: "US", province: "Wyoming" },
  { country: "United States", countryCode: "US", province: "Virgin Islands" },
  {
    country: "United States",
    countryCode: "US",
    province: "Armed Forces Americas",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Armed Forces Europe",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Armed Forces Pacific",
  },
];
